import type { CRMPhoneAPI, CRMPhoneAPIConfig } from '../types/crmphone';
import type { CRMPhoneCallState } from '../types/crmphone';
import type { TelephonyIdentifyCandidate } from '../types/telephony.events';
import { normalizePhoneDigits } from '../utils/phone';

// ENV
const WIDGET_SRC = import.meta.env.VITE_CRM_PHONE_WIDGET_SRC as string | undefined;
const FEATURE_ENABLED = String(import.meta.env.VITE_CRM_PHONE_ENABLED ?? 'false').toLowerCase() === 'true';
const DEFAULT_WS = (import.meta.env.VITE_CRM_PHONE_WS as string | undefined) ?? 'wss://assistel.qomeq.kz/ws';
const DEFAULT_DOMAIN = (import.meta.env.VITE_CRM_PHONE_DOMAIN as string | undefined) ?? 'assistel.qomeq.kz';
const DEFAULT_AUTOSTART = (import.meta.env.VITE_CRM_PHONE_AUTOSTART as string | undefined) ?? 'false';
const PRELOAD_SIP = ((import.meta.env.VITE_CRM_PHONE_PRELOAD_SIP as string | undefined) ?? 'true').toLowerCase() === 'true';
const SIP_SRC = (import.meta.env.VITE_CRM_PHONE_SIP_SRC as string | undefined) ?? 'https://assistel.qomeq.kz/vendor/sip.min.js';
const HIDE_WIDGET_TOGGLE = ((import.meta.env.VITE_CRM_PHONE_HIDE_TOGGLE as string | undefined) ?? 'true').toLowerCase() === 'true';
const CONFIG_URL = (import.meta.env.VITE_CRM_PHONE_CONFIG_URL as string | undefined) ?? '/api/v1/telephony/me';
const TURN_SERVERS_RAW = import.meta.env.VITE_CRM_PHONE_TURN_SERVERS as string | undefined; // JSON-массив ICE-серверов
const DEBUG_LOGS_ENABLED = ((import.meta.env.VITE_CRM_PHONE_DEBUG as string | undefined) ?? 'false').toLowerCase() === 'true';

const debugLog = (...args: unknown[]) => {
  if (!DEBUG_LOGS_ENABLED) {
    return;
  }
  console.info(...args);
};

const logWarn = (...args: unknown[]) => {
  console.warn(...args);
};

const safeExecute = (fn: () => void, context: string) => {
  try {
    fn();
  } catch (error) {
    logWarn(context, error);
  }
};

const SCRIPT_ID = 'crmphone-widget-script';
const pendingCustomers = new Map<string, TelephonyIdentifyCandidate>();

const normalizeKey = (phone?: string | null): string | null => normalizePhoneDigits(phone || '');

// DEV‑шим: перехватываем webhook вызовы внешнего виджета, чтобы избежать CORS в локалке
const installDevWebhookProxyShim = () => {
  try {
    if (!import.meta.env.DEV) return;
    const patchedKey = '__ab3_dev_webhook_shim__';
    if ((window as any)[patchedKey]) return;
    const rewrite = (url: string): string | null => {
      try {
        const u = new URL(url, window.location.origin);
        const host = u.host.toLowerCase();
        if (host === 'iqomeqapi.ab3.kz' && u.pathname === '/api/v1/telephony/webhook') {
          return '/api/v1/telephony/webhook';
        }
      } catch (error) {
        logWarn('[crmphone] rewrite webhook URL failed', error);
      }
      return null;
    };
    const origFetch = window.fetch.bind(window);
    (window as any).fetch = (input: RequestInfo | URL, init?: RequestInit) => {
      try {
        const url = typeof input === 'string' ? input : (input as any)?.url as string;
        const rew = rewrite(url);
        if (rew) return origFetch(rew as any, init as any);
      } catch (error) {
        logWarn('[crmphone] fetch shim failed to rewrite URL', error);
      }
      return origFetch(input as any, init as any);
    };
    try {
      const origOpen = (XMLHttpRequest.prototype.open as any).bind(XMLHttpRequest.prototype);
      (XMLHttpRequest.prototype.open as any) = function (this: XMLHttpRequest, method: string, url: string, ...rest: any[]) {
        try {
          const rew = rewrite(url);
          return origOpen.call(this, method, rew || url, ...rest);
        } catch (error) {
          logWarn('[crmphone] XHR shim failed to rewrite URL', error);
          return origOpen.call(this, method, url, ...rest);
        }
      };
    } catch (error) {
      logWarn('[crmphone] Failed to patch XMLHttpRequest for dev shim', error);
    }
    (window as any)[patchedKey] = true;
  } catch (error) {
    logWarn('[crmphone] Failed to install dev webhook proxy shim', error);
  }
};

const loadScript = (src: string, id?: string) =>
  new Promise<void>((resolve, reject) => {
    if (id && document.getElementById(id)) return resolve();
    const el = document.createElement('script');
    if (id) el.id = id;
    el.async = true;
    el.src = src;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    (document.head || document.body).appendChild(el);
  });

const ensureSip = async () => {
  if (!PRELOAD_SIP) return;
  if (typeof (window as any).SIP !== 'undefined') return;
  try {
    await loadScript(SIP_SRC, 'crmphone-sip');
  } catch (error) {
    logWarn('[crmphone] Failed to preload SIP script', error);
  }
};

const hideInternalToggle = () => {
  if (!HIDE_WIDGET_TOGGLE) return;
  const hidden = new WeakSet<HTMLElement>();
  const normalize = (s: string | null | undefined) => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const collectRoots = (): (Document | ShadowRoot)[] => {
    const roots: (Document | ShadowRoot)[] = [document];
    const all = document.querySelectorAll<HTMLElement>('*');
    for (const el of Array.from(all)) {
      const sr = (el as any).shadowRoot as ShadowRoot | undefined;
      if (sr) roots.push(sr);
    }
    return roots;
  };
  const matchesToggle = (el: HTMLElement): boolean => {
    const txt = normalize(el.textContent);
    if (!txt.includes('телефон')) return false;
    try {
      const cs = getComputedStyle(el);
      const isFixed = cs.position === 'fixed' || cs.position === 'sticky';
      const rect = el.getBoundingClientRect();
      const nearBR = rect.right > window.innerWidth - 260 && rect.bottom > window.innerHeight - 260;
      return isFixed && nearBR;
    } catch (error) {
      logWarn('[crmphone] Failed to inspect toggle candidate', error);
      return false;
    }
  };
  const applyHideStyles = (el: HTMLElement) => {
    try {
      el.style.setProperty('visibility', 'hidden', 'important');
      el.style.setProperty('opacity', '0', 'important');
      el.style.setProperty('pointer-events', 'none', 'important');
      el.style.setProperty('width', '0px', 'important');
      el.style.setProperty('height', '0px', 'important');
      el.style.setProperty('transform', 'scale(0)', 'important');
    } catch (error) {
      logWarn('[crmphone] Failed to hide toggle element', error);
    }
  };
  const tryHideOnce = (): boolean => {
    let applied = false;
    for (const root of collectRoots()) {
      const candidates = root.querySelectorAll<HTMLElement>('button, [role="button"], a, div, span, iframe');
      for (const el of Array.from(candidates)) {
        if (hidden.has(el)) continue;
        if (el.tagName === 'IFRAME') {
          try {
            const fr = el as HTMLIFrameElement;
            const cs = getComputedStyle(fr);
            const rect = fr.getBoundingClientRect();
            const nearBR = rect.right > window.innerWidth - 260 && rect.bottom > window.innerHeight - 260;
            const isFixed = cs.position === 'fixed' || cs.position === 'sticky';
            const small = rect.width <= 160 && rect.height <= 160;
            if (nearBR && isFixed && small) {
              applyHideStyles(fr as any);
              hidden.add(fr as any);
              applied = true;
            }
          } catch (error) {
            logWarn('[crmphone] Failed to hide iframe toggle', error);
          }
          continue;
        }
        if (!matchesToggle(el)) continue;
        try {
          applyHideStyles(el);
          hidden.add(el);
          applied = true;
        } catch (error) {
          logWarn('[crmphone] Failed to hide toggle element', error);
        }
      }
    }
    return applied;
  };
  tryHideOnce();
  setTimeout(tryHideOnce, 300);
  setTimeout(tryHideOnce, 1200);
  const mo = new MutationObserver(() => tryHideOnce());
  mo.observe(document.documentElement, { childList: true, subtree: true });
  setTimeout(() => mo.disconnect(), 30000);
};

const hardHidePanel = () => {
  try {
    const nodes = document.querySelectorAll<HTMLElement>('div, section, aside, iframe');
    for (const el of Array.from(nodes)) {
      try {
        const cs = getComputedStyle(el);
        if (!(cs.position === 'fixed' || cs.position === 'sticky')) continue;
        const rect = el.getBoundingClientRect();
        const nearBR = rect.right > window.innerWidth - 260 && rect.bottom > window.innerHeight - 260;
        const bigEnough = rect.width >= 220 && rect.height >= 160;
        if (!nearBR || !bigEnough) continue;
        const z = parseInt(cs.zIndex || '0', 10) || 0;
        if (z < 10) continue;
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('opacity', '0', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
        el.style.setProperty('transform', 'scale(0.98)', 'important');
      } catch (error) {
        logWarn('[crmphone] Failed to hide panel element', error);
      }
    }
  } catch (error) {
    logWarn('[crmphone] Hard hide panel iteration failed', error);
  }
};

const ensureAPI = (): CRMPhoneAPI | null => (typeof window !== 'undefined' && window.CRMPhone ? window.CRMPhone : null);

export const crmPhone = {
  isReady(): boolean { return ensureAPI() !== null; },
  async ensureLoaded(): Promise<void> {
    if (!FEATURE_ENABLED) return;
    // Не используем "this" для проверки готовности, чтобы избежать TS2683
    if (ensureAPI() || document.getElementById(SCRIPT_ID)) return;
    if (!WIDGET_SRC) return;
    // Установим DEV‑шим до загрузки виджета, чтобы перехватить его fetch/XHR
    installDevWebhookProxyShim();
    const username = '';
    const password = '';
    const ws = DEFAULT_WS;
    const domain = DEFAULT_DOMAIN;
    await ensureSip();
    // Установим ICE/TURN сервера до загрузки виджета (приоритет: ENV, затем localStorage)
    try {
      let servers: unknown | undefined;
      if (TURN_SERVERS_RAW) {
        try {
          servers = JSON.parse(TURN_SERVERS_RAW);
        } catch (error) {
          logWarn('[crmphone] Failed to parse TURN servers from env', error);
          servers = undefined;
        }
      }
      if (!Array.isArray(servers) || servers.length === 0) {
        const fromLS = localStorage.getItem('ICE_TURN_SERVERS');
        if (fromLS) {
          try {
            servers = JSON.parse(fromLS);
          } catch (error) {
            logWarn('[crmphone] Failed to parse TURN servers from storage', error);
            servers = undefined;
          }
        }
      }
      if (Array.isArray(servers) && servers.length > 0) {
        (window as any).ICE_TURN_SERVERS = servers;
        try {
          debugLog('[CRMPhone] Using TURN servers:', servers.map((s: any) => s?.urls || s).join(', '));
        } catch (logError) {
          logWarn('[crmphone] Failed to log TURN server list', logError);
        }
      }
    } catch (error) {
      logWarn('[crmphone] Failed to configure TURN servers', error);
    }
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.async = true;
    s.dataset.ws = ws;
    s.dataset.domain = domain;
    if (username) s.dataset.username = username;
    if (password) s.dataset.password = password;
    if (CONFIG_URL) { s.dataset.configUrl = CONFIG_URL; s.setAttribute('data-config-url', CONFIG_URL); }
    if (SIP_SRC) { s.dataset.sipSrc = SIP_SRC; s.setAttribute('data-sip-src', SIP_SRC); }
    const wantAutostart = String(DEFAULT_AUTOSTART).toLowerCase() === 'true';
    const canAutostart = Boolean(username && password) || Boolean(CONFIG_URL);
    s.dataset.autostart = wantAutostart && canAutostart ? 'true' : 'false';
    s.src = WIDGET_SRC!;
    document.body.appendChild(s);
    hideInternalToggle();
  },
  start(): void { ensureAPI()?.start(); },
  stop(): void { ensureAPI()?.stop(); },
  call(number: string, customer?: TelephonyIdentifyCandidate | null): void {
    if (customer) {
      const key = normalizeKey(number);
      if (key) {
        pendingCustomers.set(key, customer);
        window.setTimeout(() => {
          if (pendingCustomers.get(key) === customer) pendingCustomers.delete(key);
        }, 120000);
      }
    }
    ensureAPI()?.call(number);
  },
  consumePendingCustomer(phone?: string | null): TelephonyIdentifyCandidate | undefined {
    const key = normalizeKey(phone);
    if (!key) return undefined;
    const candidate = pendingCustomers.get(key);
    if (candidate) pendingCustomers.delete(key);
    return candidate;
  },
  answer(): void { ensureAPI()?.answer(); },
  hangup(): void { ensureAPI()?.hangup(); },
  show(): void {
    if (!this.isReady()) { this.ensureLoaded().then(() => ensureAPI()?.show()).catch(() => undefined); return; }
    ensureAPI()?.show();
  },
  hide(): void { ensureAPI()?.hide(); hardHidePanel(); },
  setConfig(cfg: CRMPhoneAPIConfig): void { ensureAPI()?.setConfig(cfg); },
  isInCall(): boolean {
    try {
      return Boolean(ensureAPI()?.isInCall?.());
    } catch (error) {
      logWarn('[crmphone] isInCall check failed', error);
      return false;
    }
  },
  getCallState(): CRMPhoneCallState | undefined {
    try {
      return ensureAPI()?.getCallState?.();
    } catch (error) {
      logWarn('[crmphone] getCallState failed', error);
      return undefined;
    }
  },
  enableUnloadGuard(enable: boolean): void {
    try {
      ensureAPI()?.enableUnloadGuard?.(enable);
    } catch (error) {
      logWarn('[crmphone] enableUnloadGuard failed', error);
    }
  },
  onCallStateChange(cb: (state: CRMPhoneCallState, info: { isInCall: boolean }) => void): void {
    try {
      if (ensureAPI()) (ensureAPI() as any).onCallStateChange = cb;
    } catch (error) {
      logWarn('[crmphone] onCallStateChange handler registration failed', error);
    }
  },
  /**
   * Попытка найти DOM‑элемент панели виджета (правый нижний угол).
   * Используется для докинга карточки поверх панели (визуальная склейка).
   */
  getWidgetPanelEl(): HTMLElement | null {
    try {
      const nodes = document.querySelectorAll<HTMLElement>('div, section, aside, iframe');
      for (const el of Array.from(nodes)) {
        const cs = getComputedStyle(el);
        const isFixed = cs.position === 'fixed' || cs.position === 'sticky';
        if (!isFixed) continue;
        const opacity = parseFloat(cs.opacity || '1');
        const isHidden = cs.visibility === 'hidden' || opacity < 0.05 || cs.pointerEvents === 'none';
        if (isHidden) continue;
        const rect = el.getBoundingClientRect();
        const nearBR = rect.right > window.innerWidth - 260 && rect.bottom > window.innerHeight - 120;
        const bigEnough = rect.width >= 220 && rect.height >= 80;
        const z = parseInt(cs.zIndex || '0', 10) || 0;
        if (nearBR && bigEnough && z >= 10) {
          return el;
        }
      }
    } catch (error) {
      logWarn('[crmphone] getWidgetPanelEl failed', error);
    }
    return null;
  },
  destroy(): void {
    try {
      safeExecute(() => ensureAPI()?.enableUnloadGuard?.(false), '[crmphone] disable unload guard failed');
      safeExecute(() => ensureAPI()?.stop?.(), '[crmphone] stop call failed');
      safeExecute(() => ensureAPI()?.hide?.(), '[crmphone] hide widget failed');

      safeExecute(() => {
        const s = document.getElementById('crmphone-widget-script');
        s?.parentNode?.removeChild(s);
      }, '[crmphone] remove widget script failed');
      safeExecute(() => {
        const sip = document.getElementById('crmphone-sip');
        sip?.parentNode?.removeChild(sip);
      }, '[crmphone] remove sip script failed');

      safeExecute(() => {
        const nodes = document.querySelectorAll<HTMLElement>('div, section, aside, iframe');
        for (const el of Array.from(nodes)) {
          const cs = getComputedStyle(el);
          const isFixed = cs.position === 'fixed' || cs.position === 'sticky';
          if (!isFixed) continue;
          const rect = el.getBoundingClientRect();
          const nearBR = rect.right > window.innerWidth - 260 && rect.bottom > window.innerHeight - 260;
          const bigEnough = rect.width >= 200 && rect.height >= 120;
          const z = parseInt(cs.zIndex || '0', 10) || 0;
          if (nearBR && bigEnough && z >= 10) {
            safeExecute(() => el.remove(), '[crmphone] remove widget DOM node failed');
          }
        }
      }, '[crmphone] cleanup widget DOM failed');

      safeExecute(() => localStorage.setItem('crmphone.panelVisible', 'false'), '[crmphone] persist panel visibility failed');
      safeExecute(() => {
        localStorage.removeItem('crmphone.username');
        localStorage.removeItem('crmphone.password');
      }, '[crmphone] clear stored credentials failed');
      safeExecute(() => { (window as any).CRMPhone = undefined; }, '[crmphone] clear global CRMPhone reference failed');
    } catch (error) {
      logWarn('[crmphone] destroy failed', error);
    }
  },
};

export default crmPhone;
